import React, { useMemo, useState } from 'react';
import { TrackerCatalyst, TrackerPermissions, TrackerPresentationFile } from '../../types';
import TrackerIcon from './TrackerIcon';
import {
  OUTCOME_LABEL, evPast, favPassed, favRead, favUnread, fmtDay, fmtLong, host, kindClass, overdue, presCls, presLabel,
  studyDesc, whenCell,
} from './helpers';

const PF_KIND: Record<string, string> = {
  poster: 'Poster', slides: 'Slides', investor_deck: 'Investor deck', publication: 'Publication',
  exhibit: 'SEC exhibit', social: 'Photo post', other: 'File',
};
import { posAdj, perspOf } from './readThrough';
import PerspectiveAnnotation from './PerspectiveAnnotation';
import ReadThroughPanel from './ReadThroughPanel';

interface Props {
  row: TrackerCatalyst;
  /** Full catalyst list, for read-through/Perspective cross-referencing (see CatalystListView). */
  allRows: TrackerCatalyst[];
  perspectiveCompany?: string;
  isWatched: boolean;
  onToggleFavorite: (id: string) => void;
  permissions: TrackerPermissions | null;
  onSuggestCorrection: (row: TrackerCatalyst) => void;
  readMap: Record<string, string> | undefined;
  onToggleRead: (id: string, on: boolean) => void;
  onOpenCatalyst: (id: string) => void;
}

/** One catalyst row + its expandable detail — the exact `.ev` / `.detail` markup from tracker.js's
 * evHTML()/detailHTML(), styled by the imported tracker.css rather than Tailwind utilities. */
const CatalystCard: React.FC<Props> = ({ row, allRows, perspectiveCompany, isWatched, onToggleFavorite, permissions, onSuggestCorrection, readMap, onToggleRead, onOpenCatalyst }) => {
  const [open, setOpen] = useState(false);
  const isDelayed = !!row.delayed;
  const isOverdue = overdue(row);
  const when = whenCell(row);
  const unread = favUnread(row, readMap, isWatched);
  const isPast = row.status === 'past';
  const showData = !!row.data && (isPast || row.data_kind === 'abstract');

  // presFilesHTML(r)/presSources(r) — tracker.js:159-184: one chip per kind of presented material (saved
  // copy > open link > gated portal note > "link pending"/"accepted" for a journal with no url yet), then
  // presented_sources as separate "Sources" chips, deduped against whatever URL a file chip already used.
  const pfRaw: TrackerPresentationFile[] = Array.isArray(row.presentation_files)
    ? row.presentation_files.filter((f) => f && (f.url || f.asset_id || f.journal)) : [];
  const filesByKind = useMemo(() => {
    const groups = new Map<string, TrackerPresentationFile>();
    const rank = (f: TrackerPresentationFile) => (f.asset_id ? 0 : f.url && !f.gated ? 1 : f.url ? 2 : 3);
    for (const f of pfRaw) {
      const k = f.kind === 'publication' ? `pub:${f.journal || f.url || ''}` : (f.kind || 'other');
      const cur = groups.get(k);
      if (!cur || rank(f) < rank(cur)) groups.set(k, f);
    }
    return [...groups.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.presentation_files]);
  const seenUrls = new Set(pfRaw.map((f) => f.url).filter(Boolean) as string[]);
  const sources: Array<{ url: string; label: string; date: string }> = [];
  for (const p of Array.isArray(row.presented_sources) ? row.presented_sources : []) {
    const s = typeof p === 'string' ? { url: p, label: '', date: '' } : { url: p?.url || '', label: p?.label || '', date: p?.date || '' };
    if (!s.url || seenUrls.has(s.url)) continue;
    seenUrls.add(s.url);
    sources.push(s);
  }

  const pills: React.ReactNode[] = [];
  if (row.signal) {
    const sigCls = row.signal === 'Practice-changing' ? 'pc' : row.signal === 'New indication' ? 'ni' : 'poc';
    pills.push(<span key="sig" className={`pill sig ${sigCls}`} title={row.signal_basis}>{row.signal}</span>);
  }
  if (isPast) {
    const o = row.outcome || 'none';
    pills.push(<span key="out" className={`pill ${o}`}>{OUTCOME_LABEL[o] || 'Reported'}</span>);
  }
  if (unread) {
    pills.push(
      <button key="unread" type="button" className="pill unread" onClick={() => onToggleRead(row.id, true)}
        title={isPast ? 'Result reported on a favorite. Click to mark as read' : 'Expected date has passed. Click to mark as read'}>
        {isPast ? 'New result' : 'Date passed'} · Mark read
      </button>,
    );
  } else if (isWatched && favPassed(row) && favRead(row, readMap)) {
    pills.push(
      <button key="readok" type="button" className="pill readok" onClick={() => onToggleRead(row.id, false)}
        title="You marked this as read. Click to mark unread">
        Read &#10003;
      </button>,
    );
  }
  if (isDelayed) pills.push(<span key="delay" className="pill delay" title={row.original_window ? `Was ${row.original_window}` : 'Timing slipped'}>Delayed</span>);
  if (isOverdue) pills.push(<span key="overdue" className="pill delay" title="Expected date passed; no outcome logged">Overdue</span>);
  if (!isPast && evPast(row)) {
    pills.push(<span key="resultspending" className="pill pending" title="Event date has passed; presented results not yet logged">Results pending</span>);
  }

  // scoreChip(r) — tracker.js:1960-1968: a P(+) badge (base estimate adjusted by related recent readouts)
  // plus a High-impact chip, shown only once there's a real probability estimate.
  const adj = useMemo(() => posAdj(allRows, row), [allRows, row]);
  if (adj) {
    const pcls = adj.adj == null ? 'mid' : adj.adj >= 70 ? 'hi' : adj.adj < 40 ? 'lo' : 'mid';
    const tip = adj.adj != null ? `${adj.adj}% chance the outcome is positive${row.pos_basis ? `: ${row.pos_basis}` : ''}` : 'No probability estimate yet';
    pills.push(
      <span key="score" className={`score pos ${pcls} tip-r`} title={tip} tabIndex={0}>
        <span className="sl">P(+)</span>{adj.adj != null ? `${adj.adj}%` : 'n/e'}
      </span>,
    );
    if (row.pos != null && row.impact === 'High') {
      pills.push(
        <span key="scoreimp" className="score imp High tip-r" title={`High impact: ${row.impact_basis || ''} · High = pivotal / value-defining; Medium = notable, not thesis-changing; Low = incremental`} tabIndex={0}>
          High impact
        </span>,
      );
    }
  }

  // perspOf(e) — tracker.js:554-601/649-659: "viewing as <company>" annotation, shown on every card.
  const persp = useMemo(() => (perspectiveCompany ? perspOf(allRows, row, perspectiveCompany) : null), [allRows, row, perspectiveCompany]);

  return (
    <article className={`ev${isDelayed ? ' delayed' : ''}${isOverdue ? ' overdue' : ''}${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="toggle"
        aria-expanded={open}
        aria-label={`Details for ${row.asset || row.company}: ${row.event || ''}`}
        onClick={() => setOpen((v) => !v)}
      />
      <div className={when.cls} title={when.title}>
        <span className={`kind ${kindClass(row.type)}`}>{row.type || 'Other'}</span>
        <span className="d">{when.d}</span>
        <span className="y">{when.y}</span>
      </div>
      <div className="main">
        <div className="side">{pills}</div>
        <div className="head">
          <button
            type="button"
            className={`star tip-l${isWatched ? '' : ' off'}`}
            aria-pressed={isWatched}
            aria-label={isWatched ? 'Remove from favorites' : 'Favorite this catalyst'}
            title={isWatched ? 'Favorited · click to remove' : 'Favorite this catalyst'}
            onClick={() => onToggleFavorite(row.id)}
          >
            <TrackerIcon name="star" />
          </button>
          <span className="asset">{row.asset || '—'}</span>
          <span className="l1">
            <span>{row.company}</span>
            {row.ticker && <span className="tk">{row.ticker}</span>}
            {Array.isArray(row.partners) && row.partners.length > 0 && <span className="ptn">with {row.partners.join(', ')}</span>}
          </span>
          {row.moa && <span className="moa">{row.moa}</span>}
        </div>

        {(row.trial || studyDesc(row)) && (
          <div className="study">
            {row.trial && <span className="sname tip-l" title={`${row.trial}${row.nct ? ` (${row.nct})` : ''}`}>{row.trial}</span>}
            <span className="sdesc">{studyDesc(row)}</span>
          </div>
        )}

        <div className="event">{row.event}</div>

        {showData && (
          <div className={`data ${row.outcome || 'pending'}${presCls(row)}${open ? '' : ' clamp'}`}>
            <span className="v"><span className="k">{presLabel(row)}</span>{row.data}</span>
            {open && row.safety && <span className="v dl"><span className="k">Safety</span>{row.safety}</span>}
            {open && row.population && <span className="v dl"><span className="k">Population</span>{row.population}</span>}
          </div>
        )}
        {!isPast && !showData && row.data && (
          <div className="data pending">
            <span className="v"><span className="k">Guidance</span>{row.data}</span>
          </div>
        )}

        {filesByKind.length > 0 && (
          <div className="pfiles">
            <span className="k">Presented material</span>
            {filesByKind.map((f, i) => {
              const lab = f.kind === 'publication' && f.journal ? f.journal : (PF_KIND[f.kind || 'other'] || 'File');
              if (!f.url && !f.asset_id) {
                // A journal entry with no link yet ("accepted" or "link pending").
                return f.journal ? (
                  <span key={i} className="pfg">
                    <span className="pfc gated" tabIndex={0} title={f.title || ''}>
                      <TrackerIcon name="file" />{f.journal} <span>{f.status === 'accepted' ? 'accepted · not yet online' : 'link pending'}</span>
                    </span>
                  </span>
                ) : null;
              }
              const saved = f.asset_id ? `/catalyst-tracker/_blob/${f.asset_id}` : '';
              const fileName = f.file_name || `${row.trial || row.asset || 'presentation'}-${f.kind || 'file'}.pdf`;
              const tip = [f.title, f.gated ? 'On a company portal that asks for a login or healthcare-professional attestation' : '', f.check].filter(Boolean).join('\n');
              return (
                <span key={i} className="pfg">
                  {saved ? (
                    <>
                      <a className="pfc saved" href={saved} target="_blank" rel="noopener noreferrer" title={`${f.title || lab}\nSaved copy on this calendar${f.archived_at ? ` (${fmtLong(f.archived_at)})` : ''}\nOpens the PDF in a new tab`}>
                        <TrackerIcon name="file" />{lab}
                      </a>
                      <a className="pfc saved pfdl" href={saved} download={fileName} title="Download the PDF" aria-label="Download the PDF">
                        <TrackerIcon name="download" />
                      </a>
                    </>
                  ) : (
                    <a className={`pfc${f.gated ? ' gated' : ''}`} href={f.url} target="_blank" rel="noopener noreferrer" title={tip || host(f.url || '')}>
                      <TrackerIcon name="file" />{lab}{f.gated ? ' · HCP portal' : ''} <span>{host(f.url || '')}</span>
                    </a>
                  )}
                </span>
              );
            })}
          </div>
        )}

        {sources.length > 0 && (
          <div className="pfiles">
            <span className="k">Sources</span>
            {sources.map((s, i) => (
              <a key={i} className="pfc" href={s.url} target="_blank" rel="noopener noreferrer" title={host(s.url)}>
                <TrackerIcon name="ext" />{s.label || host(s.url)}{s.date ? <span> {fmtDay(s.date)}</span> : null}
              </a>
            ))}
          </div>
        )}

        {!open && <ReadThroughPanel allRows={allRows} row={row} onOpen={onOpenCatalyst} />}

        {persp && perspectiveCompany && <PerspectiveAnnotation annotation={persp} company={perspectiveCompany} />}
      </div>

      {open && (
        <div className="detail">
          {row.indication && (
            <section className="dsec">
              <h4>Study</h4>
              <div className="dgrid">
                {(row.trial || row.phase) && <div><span className="k">Study</span>{[row.trial, row.phase].filter(Boolean).join(' · ')}</div>}
                {row.indication && <div><span className="k">Indication</span>{row.indication}</div>}
                {row.target && <div><span className="k">Target</span>{row.target}</div>}
                {row.modality && <div><span className="k">Modality</span>{row.modality}</div>}
                {row.nct && (
                  <div className="span2">
                    <span className="k">Registry</span>
                    <a href={`https://clinicaltrials.gov/study/${row.nct}`} target="_blank" rel="noopener noreferrer">{row.nct}</a>
                    {row.registry_status ? ` · ${row.registry_status}` : ''}
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="dsec">
            <h4>When and where</h4>
            <div className="dgrid">
              <div><span className="k">Timing</span>{row.window || fmtLong(row.date)}</div>
              {row.venue && (
                <div className="span3 where">
                  <span className="k">Venue and session</span>
                  {row.venue}{row.abstract_id ? ` · Abstract ${row.abstract_id}` : ''}{row.session_name ? ` · ${row.session_name}` : ''}
                </div>
              )}
            </div>
          </section>

          {(row.impact || row.signal || row.benchmark) && (
            <section className="dsec">
              <h4>Assessment</h4>
              <div className="dgrid">
                {row.impact && <div className="span2"><span className="k">Impact if it happens</span><b>{row.impact}</b>{row.impact_basis ? ` · ${row.impact_basis}` : ''}</div>}
                {row.signal && <div className="span2"><span className="k">Signal</span><b>{row.signal}</b>{row.signal_basis ? ` · ${row.signal_basis}` : ''}</div>}
                {row.benchmark && <div className="span2"><span className="k">Standard-of-care benchmark</span>{row.benchmark}</div>}
              </div>
            </section>
          )}

          {row.company_note && (
            <section className="dsec">
              <h4>Company</h4>
              <div className="dgrid"><div className="wide"><span className="k">Note</span>{row.company_note}</div></div>
            </section>
          )}

          {(isDelayed || isOverdue) && (
            <div className="delaybox">
              <span className="k" style={{ color: 'var(--delay)' }}>{isDelayed ? 'Delay' : 'Overdue'}</span>
              {isDelayed && row.original_window
                ? <>Guided <b>{row.original_window}</b> &rarr; now <b>{row.window || fmtLong(row.date)}</b>.{row.delay_note ? ` ${row.delay_note}` : ''}</>
                : <>Expected {fmtLong(row.date)}; no outcome logged yet.</>}
            </div>
          )}

          <div className="dfoot">
            Last verified {row.lastVerified ? fmtLong(row.lastVerified) : (row.updatedAt ? fmtLong(row.updatedAt) : '—')}
            {row.source ? ` · source: ${host(row.source)}` : ''}
          </div>
          <div className="acts">
            {row.source && (
              <a className="ib sm" href={row.source} target="_blank" rel="noopener noreferrer"><TrackerIcon name="ext" />Source</a>
            )}
            {permissions?.editor && (
              <button type="button" className="ib sm" title="Suggest outcome or correction" onClick={() => onSuggestCorrection(row)}>
                <TrackerIcon name="pencil" />Suggest a correction
              </button>
            )}
          </div>
        </div>
      )}
    </article>
  );
};

export default CatalystCard;
